/**
 * SubscriptionsAdminTab · v2.6.7 — Admin Console → Administration → Subscriptions
 *
 * Lists every rider subscription with filter by status (all / active / pending /
 * expired / cancelled). Actions:
 *   - Mark as Paid (Cash) — activates a pending sub. PIN-gated.
 *   - Cancel — revokes remaining credits. PIN-gated. NO REFUND issued
 *     (memberships are non-refundable).
 *   - Activate — manually flip status to active (e.g., comp). PIN-gated.
 */
import { useEffect, useMemo, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Search, RotateCw, BanknoteIcon, ShieldCheck, X as XIcon, AlertTriangle, Lock } from "lucide-react";
import { fmtUsd } from "@/lib/format";

const STATUS_PILL = {
  active:    "border-emerald-500/50 text-emerald-300",
  pending:   "border-orange-400/50 text-orange-300",
  cancelled: "border-red-500/50 text-red-300",
  expired:   "border-zinc-600 text-zinc-400",
};

export default function SubscriptionsAdminTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionDialog, setActionDialog] = useState(null); // { kind: 'cash'|'cancel'|'activate', sub: {} }
  const [form, setForm] = useState({ pin: "", reference: "", reason: "" });

  const reload = () => {
    setBusy(true);
    adminApi.get("/admin/subscriptions", { params: { status } })
      .then(r => setItems(r.data.items || []))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load subscriptions"))
      .finally(() => setBusy(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [status]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return items;
    return items.filter(s => (
      (s.customer_name || "").toLowerCase().includes(q)
      || (s.customer_email || "").toLowerCase().includes(q)
      || (s.plan_name || "").toLowerCase().includes(q)
      || (s.payment_method || "").toLowerCase().includes(q)
      || (s.status || "").toLowerCase().includes(q)
    ));
  }, [items, search]);

  const totals = useMemo(() => ({
    count: filtered.length,
    active: filtered.filter(s => s.status === "active").length,
    pending: filtered.filter(s => s.status === "pending").length,
    revenue: filtered.filter(s => s.status === "active").reduce((sum, s) => sum + (s.price || 0), 0),
  }), [filtered]);

  const openAction = (kind, sub) => {
    setForm({ pin: "", reference: "", reason: "" });
    setActionDialog({ kind, sub });
  };

  const submit = async () => {
    if (!actionDialog) return;
    if (!form.pin) { toast.error("Your supervisor PIN is required"); return; }
    const { kind, sub } = actionDialog;
    setBusy(true);
    try {
      if (kind === "cash") {
        await adminApi.post(`/admin/subscriptions/${sub.id}/cash-mark-paid`, { pin: form.pin, reference: form.reference });
        toast.success(`${sub.customer_name} · ${sub.plan_name} marked PAID (cash)`);
      } else if (kind === "cancel") {
        await adminApi.post(`/admin/subscriptions/${sub.id}/cancel`, { pin: form.pin, reason: form.reason });
        toast.success(`${sub.plan_name} cancelled · NO refund (non-refundable)`);
      } else if (kind === "activate") {
        await adminApi.post(`/admin/subscriptions/${sub.id}/activate`, { pin: form.pin });
        toast.success(`${sub.plan_name} activated`);
      }
      setActionDialog(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="subscriptions-admin-tab">
      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-bold flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-400"/>Rider Subscriptions</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">All subscriptions are <span className="text-orange-300 font-bold">non-refundable</span>. Use Mark Paid (Cash) to activate pending subs and Cancel to revoke remaining credits (no refund issued).</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-3 w-3 absolute left-2 top-2.5 text-zinc-500"/>
              <Input data-testid="subs-admin-search" value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search rider/plan/email" className="bg-zinc-950 border-zinc-700 text-xs h-8 pl-7 w-56"/>
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-xs h-8 w-32" data-testid="subs-admin-status"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={reload} disabled={busy} className="bg-zinc-950 border-zinc-700 h-8 text-xs"><RotateCw className="h-3 w-3 mr-1"/>Refresh</Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
          <Pill label="Total" value={totals.count}/>
          <Pill label="Active" value={totals.active} color="text-emerald-400"/>
          <Pill label="Pending cash" value={totals.pending} color={totals.pending > 0 ? "text-orange-400" : "text-zinc-300"}/>
          <Pill label="Active revenue" value={fmtUsd(totals.revenue)} color="text-amber-400"/>
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 divide-y divide-zinc-900">
        {filtered.length === 0 && <div className="p-8 text-center text-zinc-500 text-sm">No subscriptions match.</div>}
        {filtered.map(s => (
          <div key={s.id} className="p-3" data-testid={`sub-admin-row-${s.id}`}>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={`uppercase text-[10px] ${STATUS_PILL[s.status] || "border-zinc-700 text-zinc-300"}`}>{s.status}</Badge>
                  <Badge variant="outline" className="text-[10px] uppercase border-amber-400/40 text-amber-300">{s.plan_name}</Badge>
                  <span className="text-[10px] text-zinc-500 font-mono">#{s.id?.slice(0,8).toUpperCase()}</span>
                </div>
                <div className="mt-1 text-sm font-semibold">{s.customer_name || "—"} <span className="text-zinc-500 text-xs">· {s.customer_email}</span></div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  <span className="text-emerald-300 font-bold">{s.credits_remaining}</span>/{s.credits_total} credits · {s.payment_method?.toUpperCase()} · {s.payment_status}
                  {s.activated_at && <> · activated {new Date(s.activated_at).toLocaleDateString()}</>}
                  {s.expires_at && <> · expires {new Date(s.expires_at).toLocaleDateString()}</>}
                </div>
                {s.cash_reference && <div className="text-[10px] text-zinc-600 mt-0.5">Cash ref: {s.cash_reference}{s.marked_paid_by ? ` · ${s.marked_paid_by}` : ""}</div>}
                {s.cancel_reason && <div className="text-[10px] text-red-400 mt-0.5">Cancelled: {s.cancel_reason}{s.cancelled_by ? ` · ${s.cancelled_by}` : ""}</div>}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <div className="text-right">
                  <div className="text-lg font-bold meter-display text-amber-400">{fmtUsd(s.price)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">paid</div>
                </div>
                {s.status === "pending" && (
                  <Button data-testid={`sub-cash-paid-${s.id}`} size="sm" onClick={() => openAction("cash", s)} className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold text-xs"><BanknoteIcon className="h-3 w-3 mr-1"/>Mark Paid (Cash)</Button>
                )}
                {(s.status === "pending" || s.status === "expired" || s.status === "cancelled") && (
                  <Button data-testid={`sub-activate-${s.id}`} size="sm" variant="outline" onClick={() => openAction("activate", s)} className="bg-zinc-950 border-amber-400/40 text-amber-300 hover:bg-amber-400/10 h-8 text-xs"><ShieldCheck className="h-3 w-3 mr-1"/>Activate</Button>
                )}
                {s.status === "active" && (
                  <Button data-testid={`sub-cancel-${s.id}`} size="sm" variant="outline" onClick={() => openAction("cancel", s)} className="bg-zinc-950 border-red-500/40 text-red-300 hover:bg-red-500/10 h-8 text-xs"><XIcon className="h-3 w-3 mr-1"/>Cancel</Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </Card>

      {/* Action dialog */}
      <Dialog open={!!actionDialog} onOpenChange={(o) => { if (!o) setActionDialog(null); }}>
        <DialogContent className="bg-zinc-950 border-amber-400/40 text-zinc-100 max-w-md" data-testid="sub-action-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2">
              {actionDialog?.kind === "cash" && <><BanknoteIcon className="h-5 w-5"/>Mark Paid · Cash</>}
              {actionDialog?.kind === "cancel" && <><XIcon className="h-5 w-5 text-red-400"/>Cancel Subscription</>}
              {actionDialog?.kind === "activate" && <><ShieldCheck className="h-5 w-5"/>Manual Activate</>}
            </DialogTitle>
          </DialogHeader>
          {actionDialog && (
            <div className="space-y-3 text-sm">
              <Card className="bg-zinc-900 border-zinc-800 p-3 space-y-1 text-xs">
                <RowLine k="Rider" v={actionDialog.sub.customer_name}/>
                <RowLine k="Plan" v={actionDialog.sub.plan_name}/>
                <RowLine k="Price" v={fmtUsd(actionDialog.sub.price)}/>
                <RowLine k="Status" v={actionDialog.sub.status?.toUpperCase()}/>
              </Card>
              {actionDialog.kind === "cancel" && (
                <div className="bg-red-500/10 border border-red-400/30 rounded-md p-2 text-[11px] text-red-300 leading-snug flex items-start gap-2">
                  <Lock className="h-3 w-3 mt-0.5 flex-shrink-0"/>
                  <span>This subscription is <span className="font-bold">non-refundable</span>. Cancelling revokes remaining credits but <span className="font-bold">no money is returned</span> to the rider.</span>
                </div>
              )}
              {actionDialog.kind === "cash" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Cash receipt # / reference (optional)</Label>
                  <Input data-testid="sub-action-reference" value={form.reference} onChange={(e) => setForm({...form, reference: e.target.value})} placeholder="e.g. R-2026-0042" className="bg-zinc-950 border-zinc-700 text-zinc-100 mt-1"/>
                </div>
              )}
              {actionDialog.kind === "cancel" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Reason (optional)</Label>
                  <Input data-testid="sub-action-reason" value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} placeholder="e.g. duplicate purchase" className="bg-zinc-950 border-zinc-700 text-zinc-100 mt-1"/>
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Your supervisor PIN</Label>
                <Input data-testid="sub-action-pin" type="password" inputMode="numeric" value={form.pin} onChange={(e) => setForm({...form, pin: e.target.value.replace(/\D/g, "").slice(0, 8)})} placeholder="• • • • • •" className="bg-zinc-950 border-zinc-700 text-zinc-100 font-mono tracking-[0.4em] text-center mt-1"/>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setActionDialog(null)} className="bg-zinc-950 border-zinc-700">Cancel</Button>
            <Button
              data-testid="sub-action-submit"
              onClick={submit}
              disabled={busy || !form.pin}
              className={actionDialog?.kind === "cancel"
                ? "bg-red-500 hover:bg-red-400 text-white font-bold"
                : "bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"}
            >
              {actionDialog?.kind === "cash" && "Confirm Cash Payment"}
              {actionDialog?.kind === "cancel" && "Cancel Subscription"}
              {actionDialog?.kind === "activate" && "Activate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Pill({ label, value, color }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-lg font-bold ${color || "text-zinc-100"}`}>{value}</div>
    </div>
  );
}
function RowLine({ k, v }) {
  return (
    <div className="flex justify-between"><span className="text-zinc-500">{k}</span><span className="text-zinc-200 font-semibold">{v}</span></div>
  );
}
